/**
 * How a new version reaches a running page (main.tsx, hooks/useVersionCheck). Kept apart from main.tsx so the
 * rules are unit tested.
 *
 * - Never mid-work: a page on a call (/call/*), uploading (data-upload-active) or editing (data-editor-open) is
 *   never reloaded for a new version. The reload waits for the next navigation that lands somewhere none of
 *   those holds, and a small "Update available" toast says one is waiting (UpdateAvailableToast).
 * - A missing page file (vite:preloadError) reloads once per deploy, not once per tab: the guard holds the
 *   build the page was running when it reloaded, so only a reload that lands back on that same build is
 *   refused (the loop), and the next deploy's missing file reloads again.
 */

/** The <body> attributes the components doing uninterruptible work set while they work. */
export const RELOAD_BLOCKING_ATTRS = ['data-editor-open', 'data-upload-active'];

/** The event UpdateAvailableToast listens for, and the flag for a toast mounted after it fired. */
export const UPDATE_AVAILABLE_EVENT = 'db:update-available';

export function reloadBlocked(body: Pick<HTMLElement, 'hasAttribute'>, pathname: string): boolean {
  return pathname.startsWith('/call/') || RELOAD_BLOCKING_ATTRS.some((attr) => body.hasAttribute(attr));
}

export interface UpdateReloader {
  /** A new version is here: reload now, or hold the reload for the next navigation and say so once. */
  request: (reload: () => void) => void;
  /** The address changed: a held reload goes, unless the new place is mid-work too. */
  navigated: () => void;
  pending: () => boolean;
}

export function createUpdateReloader(d: { isBlocked: () => boolean; notify: () => void }): UpdateReloader {
  let held: (() => void) | null = null;
  let told = false;
  return {
    request(reload) {
      if (!d.isBlocked()) { reload(); return; }
      held = reload;
      if (!told) { told = true; d.notify(); }
    },
    navigated() {
      if (!held || d.isBlocked()) return;
      const go = held;
      held = null;
      go();
    },
    pending: () => held !== null,
  };
}

let shared: UpdateReloader | null = null;

/** The page's one reloader, on the real document and address. */
export function updateReloader(): UpdateReloader {
  shared ??= createUpdateReloader({
    isBlocked: () => reloadBlocked(document.body, window.location.pathname),
    notify: () => {
      (window as unknown as { __dbUpdateAvailable?: boolean }).__dbUpdateAvailable = true;
      window.dispatchEvent(new Event(UPDATE_AVAILABLE_EVENT));
    },
  });
  return shared;
}

export const PRELOAD_RELOAD_KEY = 'preload-err-reload';

/** A missing page file: reload, unless this tab already reloaded from this very build (it would loop). */
export function preloadReloadAllowed(storedBuild: string | null, runningBuild: string): boolean {
  return storedBuild !== runningBuild;
}
